import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Sparkline } from '@/components/charts/Sparkline';

/**
 * The card header pattern used across the panels: title, muted subtitle, optional action on the
 * right.
 *
 * Extracted rather than repeated so the rhythm stays identical everywhere — the moment two panels
 * differ by four pixels of padding, the page stops looking designed and starts looking assembled.
 */
export function SectionCard({
  title,
  subtitle,
  action,
  children,
  className,
}: {
  title: string;
  subtitle?: string | undefined;
  action?: ReactNode;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <Card className={`p-6 ${className ?? ''}`}>
      {/* Stacked on a phone: beside a title, a group of buttons was squeezed into a column of
          its own and the subtitle into a sliver next to it. */}
      <div className="mb-5 flex flex-col items-start gap-3 sm:flex-row sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          {subtitle !== undefined && (
            <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

/** The small rounded count/label chip that sits in a card's top-right corner. */
export function Pill({
  children,
  tone = 'default',
}: {
  children: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const tones = {
    default: 'border-primary/30 bg-primary/10 text-primary',
    success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
    warning: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
    danger: 'border-danger/30 bg-danger/10 text-danger',
  } as const;

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/** A status with a dot, as in the reference's ONLINE pill. */
export function StatusPill({
  label,
  tone,
}: {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'muted';
}) {
  const tones = {
    success: ['bg-emerald-500/10 text-emerald-500', 'bg-emerald-500'],
    warning: ['bg-amber-500/10 text-amber-500', 'bg-amber-500'],
    danger: ['bg-danger/10 text-danger', 'bg-danger'],
    muted: ['bg-muted text-muted-foreground', 'bg-muted-foreground'],
  } as const;

  const [chip, dot] = tones[tone];

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${chip}`}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

/** A tinted rounded-square icon container, the reference's metric marker. */
export function IconTile({
  icon: Icon,
  colour,
  size = 'md',
}: {
  icon: LucideIcon;
  /** A tailwind text colour class; the background is derived from it at low opacity. */
  colour: string;
  size?: 'sm' | 'md';
}) {
  const box = size === 'sm' ? 'size-8' : 'size-10';
  const glyph = size === 'sm' ? 'size-4' : 'size-5';

  return (
    <span
      className={`inline-flex ${box} shrink-0 items-center justify-center rounded-xl bg-current/10 ${colour}`}
    >
      <Icon className={glyph} />
    </span>
  );
}

/**
 * A metric with its own sparkline, as in the reference's Resource Usage panel.
 *
 * ## A null value is "no reading", not zero
 *
 * The rates come from differencing counters, so the first sample after a server is added has
 * none — and a server that has never been reached has none at all. Showing `0%` would report an
 * idle machine, which is the specific wrong answer somebody would act on.
 */
export function MetricRow({
  icon,
  colour,
  strokeColour,
  label,
  description,
  value,
  series,
  max,
}: {
  icon: LucideIcon;
  colour: string;
  /** A CSS colour for the sparkline stroke, matching the icon's hue. */
  strokeColour: string;
  label: string;
  description: string;
  value: number | null;
  series: (number | null)[];
  max?: number | undefined;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <IconTile icon={icon} colour={colour} />
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-tight">{label}</p>
          <p className="truncate text-sm text-muted-foreground">{description}</p>
        </div>
        <p className="shrink-0 text-xl font-semibold tabular-nums">
          {value === null ? (
            <span className="text-sm font-normal text-muted-foreground">no reading yet</span>
          ) : (
            `${value.toFixed(2)}%`
          )}
        </p>
      </div>
      <Sparkline values={series} color={strokeColour} max={max} height={44} />
    </div>
  );
}
